/**
 * The steps a couple takes, the same for every venue (docs/funnel-stages.spec.md),
 * in order. Stored as JSON keys only, so no Prisma enum mirrors them; new stored
 * values use American spelling (`canceled`), unlike the older lifecycle values.
 */
export const FUNNEL_STAGES = [
  'inquiry',
  'qualified',
  'corporate_reply',
  'venue_reply',
  'tour_scheduled',
  'open_house_rsvp',
  'toured',
  'proposal_sent',
  'date_on_hold',
  'contract_signed',
  'deposit_paid',
  'booked',
  'contract_fully_paid',
  'event_day',
] as const

export type FunnelStage = (typeof FUNNEL_STAGES)[number]

/** The two ways a couple leaves the order: an inquiry that went nowhere, a booking let go. */
export const FUNNEL_OUTCOMES = ['lost', 'canceled'] as const

export type FunnelOutcome = (typeof FUNNEL_OUTCOMES)[number]

/** A stage or an outcome: anything a source can say a couple reached. */
export type FunnelTarget = FunnelStage | FunnelOutcome

/** How each reads on screen. */
export const FUNNEL_TARGET_LABELS: Readonly<Record<FunnelTarget, string>> = {
  inquiry: 'Inquiry',
  qualified: 'Qualified',
  corporate_reply: 'Corporate reply',
  venue_reply: 'Venue reply',
  tour_scheduled: 'Tour scheduled',
  open_house_rsvp: 'Open house RSVP',
  toured: 'Toured',
  proposal_sent: 'Proposal sent',
  date_on_hold: 'Date on hold',
  contract_signed: 'Contract signed',
  deposit_paid: 'Deposit paid',
  booked: 'Booked',
  contract_fully_paid: 'Contract fully paid',
  event_day: 'Event day',
  lost: 'Lost',
  canceled: 'Canceled',
}

/**
 * What a GoHighLevel connection can say a couple reached, in order. Inquiry is
 * every lead, so it isn't mapped; Event day and Canceled come only from the
 * venue's own events.
 */
export const GHL_FUNNEL_TARGETS = [
  'qualified',
  'corporate_reply',
  'venue_reply',
  'tour_scheduled',
  'open_house_rsvp',
  'toured',
  'proposal_sent',
  'date_on_hold',
  'contract_signed',
  'deposit_paid',
  'booked',
  'contract_fully_paid',
  'lost',
] as const satisfies readonly FunnelTarget[]

export type GhlFunnelTarget = (typeof GHL_FUNNEL_TARGETS)[number]

/** What the venue's own events always record, so they are always tracked. */
export const EVENT_FUNNEL_TARGETS = [
  'inquiry',
  'date_on_hold',
  'booked',
  'event_day',
  'lost',
  'canceled',
] as const satisfies readonly FunnelTarget[]

/**
 * What the venue's own proposals, contracts and payments record (docs/sales.spec.md),
 * tracked at a venue once it has sent a proposal.
 */
export const SALES_FUNNEL_TARGETS = ['proposal_sent', 'contract_signed', 'deposit_paid', 'contract_fully_paid'] as const satisfies readonly FunnelStage[]

/** Every stage and outcome, in order: the stages, then Lost and Canceled. */
export const FUNNEL_TARGETS = [...FUNNEL_STAGES, ...FUNNEL_OUTCOMES] as const satisfies readonly FunnelTarget[]

/**
 * The two routes to a tour. The pipeline counts them as one step, and a
 * HoneyBook meeting is one of them (`HONEYBOOK_MEETING_TARGETS`).
 */
export const FUNNEL_TOUR_ROUTES = ['tour_scheduled', 'open_house_rsvp'] as const satisfies readonly FunnelStage[]

/**
 * The steps the pipeline narrows at (docs/pipeline-view.spec.md, "Main steps"),
 * in order. Tour scheduled and Open house RSVP are one step: either route to a
 * tour ({@link FUNNEL_TOUR_ROUTES}). Every other stage is a step along the way,
 * one a couple may skip, so it never becomes the base a main step is measured
 * against: Booked is out of the couples who toured, never out of those who put
 * a date on hold.
 */
export const FUNNEL_MAIN_STEPS = [
  'inquiry',
  'qualified',
  'tour_scheduled',
  'open_house_rsvp',
  'toured',
  'booked',
  'event_day',
] as const satisfies readonly FunnelStage[]

export type FunnelMainStep = (typeof FUNNEL_MAIN_STEPS)[number]

const MAIN_STEPS: ReadonlySet<FunnelTarget> = new Set(FUNNEL_MAIN_STEPS)

/** Whether a stage is one of the pipeline's main steps rather than a step along the way. */
export function isFunnelMainStep(target: FunnelTarget): target is FunnelMainStep {
  return MAIN_STEPS.has(target)
}

/**
 * The counts the pipeline prices from what marketing cost: what a marketer
 * reports as cost per inquiry, per tour and per booking. Nothing else gets a
 * cost, since spend divided by a step few couples take says nothing.
 */
export const FUNNEL_PRICED_TARGETS = ['inquiry', 'toured', 'booked'] as const satisfies readonly FunnelStage[]

const PRICED: ReadonlySet<FunnelTarget> = new Set(FUNNEL_PRICED_TARGETS)

/** Whether the pipeline puts a cost on a count of this target. */
export function isFunnelPriced(target: FunnelTarget): boolean {
  return PRICED.has(target)
}
